import { useState } from 'react';
import {
  useListPositions,
  useGetPortfolioSummary,
  useCreatePosition,
  useUpdatePosition,
  useDeletePosition,
} from '@workspace/api-client-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { StatCard } from '@/components/stat-card';
import { Spinner } from '@/components/ui/spinner';
import { Plus, Trash2, DollarSign, Target, TrendingUp, Pencil, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';

interface EditDraft {
  id: number;
  entryPrice: string;
  shares: string;
  notes: string;
}

export default function PortfolioPage() {
  const queryClient = useQueryClient();
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    marketQuestion: '',
    platform: 'polymarket',
    side: 'YES' as 'YES' | 'NO',
    entryPrice: '',
    shares: '',
    notes: '',
  });
  const [editDraft, setEditDraft] = useState<EditDraft | null>(null);
  const [closedOutcomeFilter, setClosedOutcomeFilter] = useState<'all' | 'correct' | 'incorrect'>('all');

  const { data: summary, isLoading: summaryLoading } = useGetPortfolioSummary();
  const { data: positionsData, isLoading: positionsLoading } = useListPositions({ status: 'all', limit: 100 });
  const positions = positionsData?.positions;
  const createPosition = useCreatePosition();
  const updatePosition = useUpdatePosition();
  const deletePosition = useDeletePosition();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    createPosition.mutate(
      {
        data: {
          marketQuestion: formData.marketQuestion,
          platform: formData.platform as any,
          side: formData.side,
          entryPrice: Number(formData.entryPrice),
          shares: Number(formData.shares),
          notes: formData.notes || undefined,
          marketId: `manual-${Date.now()}`,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
          queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
          setFormData({
            marketQuestion: '',
            platform: 'polymarket',
            side: 'YES',
            entryPrice: '',
            shares: '',
            notes: '',
          });
          setShowAddForm(false);
        },
      }
    );
  };

  const handleToggleStatus = (id: number, currentStatus: string) => {
    updatePosition.mutate(
      {
        id,
        data: {
          status: currentStatus === 'open' ? 'closed' : 'open',
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
          queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
        },
      }
    );
  };

  const handleReopen = (id: number) => {
    updatePosition.mutate(
      {
        id,
        data: {
          status: 'open',
          closedAt: null,
          outcome: null,
          currentPrice: null,
          pnl: null,
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
          queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
        },
      }
    );
  };

  const handleUpdateOutcome = (id: number, outcome: string) => {
    updatePosition.mutate(
      {
        id,
        data: { outcome: outcome as any },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
          queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
        },
      }
    );
  };

  const handleDelete = (id: number) => {
    if (confirm('Are you sure you want to delete this position?')) {
      deletePosition.mutate(
        { id },
        {
          onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
            queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
          },
        }
      );
    }
  };

  const handleOpenEdit = (position: NonNullable<typeof positions>[number]) => {
    setEditDraft({
      id: position.id,
      entryPrice: String(position.entryPrice),
      shares: String(position.shares),
      notes: position.notes ?? '',
    });
  };

  const handleSaveEdit = () => {
    if (!editDraft) return;
    const entryPrice = parseFloat(editDraft.entryPrice);
    const shares = parseFloat(editDraft.shares);
    if (isNaN(entryPrice) || isNaN(shares)) return;

    updatePosition.mutate(
      { id: editDraft.id, data: { entryPrice, shares, notes: editDraft.notes || undefined } },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: ['/api/positions'] });
          queryClient.invalidateQueries({ queryKey: ['/api/portfolio/summary'] });
          setEditDraft(null);
        },
      }
    );
  };

  if (summaryLoading || positionsLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  const stats = summary || {
    totalPnl: 0,
    winRate: 0,
    aiAccuracy: 0,
    openPositions: 0,
  };

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 pb-24 lg:pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Portfolio</h1>
          <p className="text-muted-foreground mt-1">
            Track your positions and performance
          </p>
        </div>
        <Button onClick={() => setShowAddForm(!showAddForm)}>
          <Plus className="mr-2 h-4 w-4" />
          Add Position
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <StatCard
          label="Total P&L"
          value={`$${(stats.totalPnl ?? 0).toFixed(2)}`}
          icon={DollarSign}
          trend={(stats.totalPnl ?? 0) >= 0 ? 'up' : 'down'}
        />
        <StatCard
          label="Win Rate"
          value={`${Math.round(stats.winRate ?? 0)}%`}
          icon={Target}
          trend={(stats.winRate ?? 0) >= 50 ? 'up' : 'down'}
        />
        <StatCard
          label="AI Accuracy"
          value={`${Math.round((summary as any)?.aiAccuracyRate ?? 0)}%`}
          icon={TrendingUp}
          trend="neutral"
        />
      </div>

      {/* Add Position Form */}
      <Collapsible open={showAddForm} onOpenChange={setShowAddForm}>
        <CollapsibleContent>
          <Card>
            <CardHeader>
              <CardTitle>Add Position</CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="marketQuestion">Market Question</Label>
                    <Input
                      id="marketQuestion"
                      value={formData.marketQuestion}
                      onChange={(e) =>
                        setFormData({ ...formData, marketQuestion: e.target.value })
                      }
                      placeholder="Will Bitcoin hit $100k in 2024?"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="platform">Platform</Label>
                    <Select
                      value={formData.platform}
                      onValueChange={(value) =>
                        setFormData({ ...formData, platform: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="polymarket">Polymarket</SelectItem>
                        <SelectItem value="kalshi">Kalshi</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="side">Side</Label>
                    <Select
                      value={formData.side}
                      onValueChange={(value: 'YES' | 'NO') =>
                        setFormData({ ...formData, side: value })
                      }
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="YES">YES</SelectItem>
                        <SelectItem value="NO">NO</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="entryPrice">Entry Price (0–1)</Label>
                    <Input
                      id="entryPrice"
                      type="number"
                      step="0.01"
                      min={0}
                      max={1}
                      value={formData.entryPrice}
                      onChange={(e) =>
                        setFormData({ ...formData, entryPrice: e.target.value })
                      }
                      placeholder="0.65"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="shares">Shares</Label>
                    <Input
                      id="shares"
                      type="number"
                      value={formData.shares}
                      onChange={(e) =>
                        setFormData({ ...formData, shares: e.target.value })
                      }
                      placeholder="100"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) =>
                      setFormData({ ...formData, notes: e.target.value })
                    }
                    placeholder="Why I took this position..."
                    rows={3}
                  />
                </div>

                <div className="flex gap-3">
                  <Button type="submit" disabled={createPosition.isPending}>
                    {createPosition.isPending ? (
                      <>
                        <Spinner size="sm" className="mr-2" />
                        Adding...
                      </>
                    ) : (
                      'Add Position'
                    )}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setShowAddForm(false)}
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </CollapsibleContent>
      </Collapsible>

      {/* Open Positions Table */}
      {(() => {
        const openPositions = positions?.filter((p) => p.status === 'open') ?? [];
        return (
          <Card>
            <CardHeader>
              <CardTitle>Open Positions</CardTitle>
            </CardHeader>
            <CardContent>
              {openPositions.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-3 font-medium">Market</th>
                        <th className="p-3 font-medium">Side</th>
                        <th className="p-3 font-medium">Entry</th>
                        <th className="p-3 font-medium">Current</th>
                        <th className="p-3 font-medium">P&L</th>
                        <th className="p-3 font-medium">Status</th>
                        <th className="p-3 font-medium">Outcome</th>
                        <th className="p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {openPositions.map((position) => {
                        const pnl = (position as any).currentPnl || 0;
                        return (
                          <tr key={position.id} className="border-b border-border last:border-0 text-sm">
                            <td className="p-3 max-w-xs truncate">{position.marketQuestion}</td>
                            <td className="p-3">
                              <span
                                className={cn(
                                  'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                                  position.side === 'YES' ? 'badge-yes' : 'badge-no'
                                )}
                              >
                                {position.side}
                              </span>
                            </td>
                            <td className="p-3">{Math.round(Number(position.entryPrice) * 100)}¢</td>
                            <td className="p-3">
                              {position.currentPrice ? `${Math.round(Number(position.currentPrice) * 100)}¢` : '—'}
                            </td>
                            <td
                              className={cn(
                                'p-3 font-semibold',
                                pnl >= 0 ? 'text-emerald-600' : 'text-red-600'
                              )}
                            >
                              {pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`}
                            </td>
                            <td className="p-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleToggleStatus(position.id, position.status)}
                              >
                                Open
                              </Button>
                            </td>
                            <td className="p-3">
                              <Select
                                value={position.outcome || 'pending'}
                                onValueChange={(value) => handleUpdateOutcome(position.id, value)}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="correct">Correct</SelectItem>
                                  <SelectItem value="incorrect">Incorrect</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(position)}
                                className="h-8 w-8 p-0"
                                title="Edit position"
                              >
                                <Pencil className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(position.id)}
                                className="h-8 w-8 p-0"
                                title="Delete position"
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>No open positions</p>
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Closed Positions Table */}
      {(() => {
        const allClosed = positions?.filter((p) => p.status === 'closed') ?? [];
        const correctCount = allClosed.filter((p) => p.outcome === 'correct').length;
        const incorrectCount = allClosed.filter((p) => p.outcome === 'incorrect').length;
        const filteredClosed =
          closedOutcomeFilter === 'all'
            ? allClosed
            : allClosed.filter((p) => (p.outcome ?? 'pending') === closedOutcomeFilter);
        return (
          <Card>
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <CardTitle>Closed Trades</CardTitle>
                {/* Outcome filter chips — hidden when there are no closed positions */}
                {allClosed.length > 0 && (
                  <div className="flex gap-2" data-testid="outcome-filter-chips">
                    {(['all', 'correct', 'incorrect'] as const).map((f) => (
                      <Button
                        key={f}
                        size="sm"
                        variant={closedOutcomeFilter === f ? 'default' : 'outline'}
                        onClick={() => setClosedOutcomeFilter(f)}
                        className="capitalize"
                      >
                        {f === 'all'
                          ? `All (${allClosed.length})`
                          : f === 'correct'
                          ? `Correct (${correctCount})`
                          : `Incorrect (${incorrectCount})`}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            </CardHeader>
            <CardContent>
              {filteredClosed.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="border-b border-border">
                      <tr className="text-left text-sm text-muted-foreground">
                        <th className="p-3 font-medium">Market</th>
                        <th className="p-3 font-medium">Side</th>
                        <th className="p-3 font-medium">Entry</th>
                        <th className="p-3 font-medium">Current</th>
                        <th className="p-3 font-medium">P&L</th>
                        <th className="p-3 font-medium">Status</th>
                        <th className="p-3 font-medium">Outcome</th>
                        <th className="p-3 font-medium">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClosed.map((position) => {
                        const pnl = (position as any).currentPnl || 0;
                        return (
                          <tr key={position.id} className="border-b border-border last:border-0 text-sm">
                            <td className="p-3 max-w-xs truncate">{position.marketQuestion}</td>
                            <td className="p-3">
                              <span
                                className={cn(
                                  'inline-flex px-2 py-0.5 rounded text-xs font-medium',
                                  position.side === 'YES' ? 'badge-yes' : 'badge-no'
                                )}
                              >
                                {position.side}
                              </span>
                            </td>
                            <td className="p-3">{Math.round(Number(position.entryPrice) * 100)}¢</td>
                            <td className="p-3">
                              {position.currentPrice ? `${Math.round(Number(position.currentPrice) * 100)}¢` : '—'}
                            </td>
                            <td
                              className={cn(
                                'p-3 font-semibold',
                                pnl >= 0 ? 'text-emerald-600' : 'text-red-600'
                              )}
                            >
                              {pnl >= 0 ? `+$${pnl.toFixed(2)}` : `-$${Math.abs(pnl).toFixed(2)}`}
                            </td>
                            <td className="p-3">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleReopen(position.id)}
                              >
                                Reopen
                              </Button>
                            </td>
                            <td className="p-3">
                              <Select
                                value={position.outcome || 'pending'}
                                onValueChange={(value) => handleUpdateOutcome(position.id, value)}
                              >
                                <SelectTrigger className="w-32 h-8">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="correct">Correct</SelectItem>
                                  <SelectItem value="incorrect">Incorrect</SelectItem>
                                </SelectContent>
                              </Select>
                            </td>
                            <td className="p-3 flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleOpenEdit(position)}
                                className="h-8 w-8 p-0"
                                title="Edit position"
                              >
                                <Pencil className="h-4 w-4 text-muted-foreground" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDelete(position.id)}
                                className="h-8 w-8 p-0"
                                title="Delete position"
                              >
                                <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                              </Button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <p>
                    {allClosed.length === 0
                      ? 'No closed trades yet'
                      : 'No trades match the selected filter'}
                  </p>
                  {allClosed.length > 0 && closedOutcomeFilter !== 'all' && (
                    <Button
                      variant="link"
                      size="sm"
                      className="mt-2"
                      onClick={() => setClosedOutcomeFilter('all')}
                    >
                      Reset filter
                    </Button>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        );
      })()}

      {/* Edit Position Dialog */}
      <Dialog open={!!editDraft} onOpenChange={(open) => { if (!open) setEditDraft(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Edit Position</DialogTitle>
          </DialogHeader>
          {editDraft && (
            <div className="space-y-4 py-2">
              <div className="space-y-2">
                <Label htmlFor="edit-entry">Entry Price (0–1)</Label>
                <Input
                  id="edit-entry"
                  type="number"
                  step="0.01"
                  min="0"
                  max="1"
                  value={editDraft.entryPrice}
                  onChange={(e) => setEditDraft({ ...editDraft, entryPrice: e.target.value })}
                  placeholder="0.65"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-shares">Shares</Label>
                <Input
                  id="edit-shares"
                  type="number"
                  value={editDraft.shares}
                  onChange={(e) => setEditDraft({ ...editDraft, shares: e.target.value })}
                  placeholder="100"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-notes">Notes (optional)</Label>
                <Textarea
                  id="edit-notes"
                  value={editDraft.notes}
                  onChange={(e) => setEditDraft({ ...editDraft, notes: e.target.value })}
                  placeholder="Why I took this position..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDraft(null)}>
              <X className="mr-2 h-4 w-4" />
              Cancel
            </Button>
            <Button onClick={handleSaveEdit} disabled={updatePosition.isPending}>
              {updatePosition.isPending ? (
                <><Spinner size="sm" className="mr-2" />Saving…</>
              ) : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
